import { gql } from '@apollo/client';

const AGENT_SKILL_FRAGMENT = gql`
    fragment AgentSkillFields on AgentSkillType {
        id
        createdAt
        updatedAt
        skillKey
    }
`;

const AGENT_KNOWLEDGE_FILE_FRAGMENT = gql`
    fragment AgentKnowledgeFileFields on AgentKnowledgeFileType {
        id
        createdAt
        updatedAt
        fileName
        fileUrl
        isSynced
        sourceType
        sourceItemId
        sourceSyncedAt
    }
`;

const AGENT_PROCESSING_RECORD_SIMPLE_FRAGMENT = gql`
    fragment AgentProcessingRecordSimpleFields on AgentProcessingRecordType {
        id
        createdAt
        updatedAt
        finishedAt
        inputData
        outputData
        outputText
    }
`;

const AGENT_FRAGMENT = gql`
    ${AGENT_SKILL_FRAGMENT}
    ${AGENT_KNOWLEDGE_FILE_FRAGMENT}
    fragment AgentFields on AgentType {
        id
        createdAt
        updatedAt
        name
        description
        enabled
        instructions {
            label
            body
        }
        erpToolProvider {
            key
            integrationId
        }
        masterDataToolProvider {
            channelId
            documentType
        }
        sharepointToolProvider {
            integrationId
            integrationName
            siteId
            siteName
            driveId
            driveName
            selectedItems {
                id
                path
            }
            syncStatus
            syncError
            lastSyncedAt
        }
        sftpKnowledgeSource {
            sftpConfigId
            path
            syncStatus
            syncError
            lastSyncedAt
        }
        knowledgeUrls
        knowledgeUrlStatuses {
            url
            status
            error
            syncedAt
        }
        orderHistoryToolProvider {
            enabled
        }
        defaultChannels {
            orderChannelId
            orderConfirmationChannelId
            rfqChannelId
            invoiceChannelId
            deliveryNoteChannelId
        }
        attachmentsEnabled
        deliveryMethod
        outputInstructions {
            label
            body
        }
        skills {
            ...AgentSkillFields
        }
        knowledgeFiles {
            ...AgentKnowledgeFileFields
        }
        smartInboxIntegrations {
            id
            name
            mailInbox
        }
        team {
            id
            name
            isDefault
        }
    }
`;

const AGENT_PROCESSING_RECORD_FRAGMENT = gql`
    ${AGENT_FRAGMENT}
    fragment AgentProcessingRecordFields on AgentProcessingRecordType {
        id
        createdAt
        updatedAt
        finishedAt
        inputData
        outputData
        outputText
        trace
        userRatingHelpful
        userRatingAt
        userRatingFeedback
        userRatingReasons
        userRatingBy {
            id
            email
            firstName
            lastName
        }
        agent {
            ...AgentFields
        }
    }
`;

// Queries

export const GET_AGENTS = gql`
    ${AGENT_FRAGMENT}
    query GetAgents {
        agents {
            ...AgentFields
        }
    }
`;

export const GET_PERSONAL_MAILBOX_OWNERS = gql`
    query GetPersonalMailboxOwners {
        personalSmartInboxIntegrations {
            id
            enabled
            createdBy {
                id
                email
                firstName
                lastName
            }
        }
    }
`;

export const GET_AGENT_MAILBOX_LINKS = gql`
    query GetAgentMailboxLinks {
        agents {
            id
            name
            smartInboxIntegrations {
                id
            }
        }
    }
`;

export const GET_AGENT = gql`
    ${AGENT_FRAGMENT}
    query GetAgent($id: UUID!) {
        agent(id: $id) {
            ...AgentFields
        }
    }
`;

export const GET_AGENT_PROCESSING_RECORDS = gql`
    ${AGENT_PROCESSING_RECORD_SIMPLE_FRAGMENT}
    query GetAgentProcessingRecords(
        $agentId: UUID
        $offset: Int
        $limit: Int
        $isTesting: Boolean
        $search: String
        $filters: [TableFilter]
    ) {
        filteredAgentProcessingRecordsList(
            agentId: $agentId
            offset: $offset
            limit: $limit
            isTesting: $isTesting
            search: $search
            filters: $filters
        ) {
            records {
                ...AgentProcessingRecordSimpleFields
                agent {
                    id
                }
                stepRun {
                    id
                    createdAt
                    stepName
                    manualOnly
                    executionStatus
                    error
                    context
                }
                createdBy {
                    id
                    email
                    firstName
                    lastName
                }
            }
            totalCount
        }
    }
`;

export const GET_AGENT_PROCESSING_RECORD = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    query GetAgentProcessingRecord($id: UUID!) {
        agentProcessingRecord(id: $id) {
            ...AgentProcessingRecordFields
            stepRun {
                id
                stepName
                manualOnly
                executionStatus
                error
                context
            }
            createdBy {
                id
                email
                firstName
                lastName
            }
        }
    }
`;

export const GET_AVAILABLE_SKILLS = gql`
    query GetAvailableSkills {
        availableSkills {
            name
            key
            description
            requiredToolNames
        }
    }
`;

export const GET_AVAILABLE_TOOL_PROVIDERS = gql`
    query GetAvailableToolProviders {
        availableToolProviders {
            name
            key
            toolNames
        }
    }
`;

export const GET_AVAILABLE_SMART_INBOX_TRIGGERS = gql`
    query GetAvailableSmartInboxTriggers {
        availableSmartInboxTriggers {
            id
            name
            agentIds
        }
    }
`;

export const GET_AVAILABLE_SFTP_CONFIGS = gql`
    query GetAvailableSftpConfigs {
        availableSftpConfigs {
            id
            host
            user
        }
    }
`;

// Mutations

export const CREATE_AGENT = gql`
    ${AGENT_FRAGMENT}
    mutation CreateAgent($input: CreateAgentInput!) {
        createAgent(input: $input) {
            agent {
                ...AgentFields
            }
        }
    }
`;

export const UPDATE_AGENT = gql`
    ${AGENT_FRAGMENT}
    mutation UpdateAgent($id: UUID!, $input: UpdateAgentInput!) {
        updateAgent(id: $id, input: $input) {
            agent {
                ...AgentFields
            }
        }
    }
`;

export const DELETE_AGENT = gql`
    mutation DeleteAgent($id: UUID!) {
        deleteAgent(id: $id) {
            success
        }
    }
`;

export const TEST_AGENT = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation TestAgent($agentId: UUID!, $data: EmailInputDataInput!) {
        testAgent(agentId: $agentId, data: $data) {
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const UPLOAD_AGENT_KNOWLEDGE_FILE = gql`
    ${AGENT_KNOWLEDGE_FILE_FRAGMENT}
    mutation UploadAgentKnowledgeFile($agentId: UUID!, $file: Upload!) {
        uploadAgentKnowledgeFile(agentId: $agentId, file: $file) {
            knowledgeFile {
                ...AgentKnowledgeFileFields
            }
        }
    }
`;

export const DELETE_AGENT_KNOWLEDGE_FILE = gql`
    mutation DeleteAgentKnowledgeFile($id: UUID!) {
        deleteAgentKnowledgeFile(id: $id) {
            success
        }
    }
`;

export const UPLOAD_AGENT_FILE = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation UploadAgentFile($agentId: UUID!, $file: Upload!) {
        agentFileUpload(input: { agentId: $agentId, file: $file }) {
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const DELETE_AGENT_PROCESSING_RECORD = gql`
    mutation DeleteAgentProcessingRecord($id: UUID!) {
        deleteAgentProcessingRecord(id: $id) {
            success
        }
    }
`;

export const RETRY_AGENT_PROCESSING_RECORD_STEP_RUN = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation RetryAgentProcessingRecordStepRun($recordId: UUID!, $force: Boolean) {
        retryAgentProcessingRecordStepRun(recordId: $recordId, force: $force) {
            success
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const REPROCESS_AGENT_PROCESSING_RECORD = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation ReprocessAgentProcessingRecord($recordId: UUID!) {
        reprocessAgentProcessingRecord(recordId: $recordId) {
            success
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const REUPLOAD_AGENT_PROCESSING_RECORD_FOR_TESTING = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation ReuploadAgentProcessingRecordForTesting($recordId: UUID!) {
        reuploadAgentProcessingRecordForTesting(recordId: $recordId) {
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const RATE_AGENT_PROCESSING_RECORD = gql`
    ${AGENT_PROCESSING_RECORD_FRAGMENT}
    mutation RateAgentProcessingRecord($recordId: UUID!, $helpful: Boolean) {
        rateAgentProcessingRecord(recordId: $recordId, helpful: $helpful) {
            record {
                ...AgentProcessingRecordFields
            }
        }
    }
`;

export const CONNECT_PERSONAL_SMART_INBOX_TO_AGENT = gql`
    mutation ConnectPersonalSmartInboxToAgent($agentId: UUID!, $connect: Boolean!) {
        connectPersonalSmartInboxToAgent(agentId: $agentId, connect: $connect) {
            success
        }
    }
`;

export const SET_AGENT_ENABLED = gql`
    mutation SetAgentEnabled($id: UUID!, $enabled: Boolean!) {
        setAgentEnabled(id: $id, enabled: $enabled) {
            agent {
                id
                enabled
            }
        }
    }
`;
